import { ImprovementGoal, Recommendation, Survey } from "@/types";

const DAY = 1000 * 3600 * 24;
const WEEK = DAY * 7;
const FOLLOW_UP_DAYS = 14;
const MAX_ALERTS = 3;

export type CategoryScores = Record<string, number>;

export type Alert = {
  key: "alertStreak" | "alertFollowUp" | "alertRecommendations";
  values: (string | number)[];
  href: string;
};

export type GoalPhase = "planning" | "execution" | "conclusion" | "finished";

const isInYear = (date: Date | string | undefined, year: number) => !!date && new Date(date).getFullYear() === year;

export const yearStats = (surveys: Survey[], goals: ImprovementGoal[], now = new Date()) => {
  const year = now.getFullYear();
  const surveysThisYear = surveys.filter((s) => isInYear(s.startDate, year));
  return {
    surveys: surveysThisYear.length,
    responses: surveysThisYear.reduce((sum, s) => sum + (s.respondents || 0), 0),
    goals: goals.filter((g) => isInYear(g.registered, year)).length,
  };
};

export const lowestCategory = (scores: CategoryScores | undefined) => {
  const entries = Object.entries(scores || {});
  if (entries.length === 0) {
    return null;
  }
  return entries.reduce((lowest, entry) => (entry[1] < lowest[1] ? entry : lowest))[0];
};

const byEndDateDesc = (a: Survey, b: Survey) => b.endDate.localeCompare(a.endDate);

// The finished surveys the streak is counted from: newest first, same type as the newest.
export const streakCandidates = (surveys: Survey[], limit = 5) => {
  const finished = surveys.filter((s) => s.status === "finished").sort(byEndDateDesc);
  if (finished.length === 0) {
    return [];
  }
  return finished.filter((s) => s.surveyType === finished[0].surveyType).slice(0, limit);
};

export const lowestStreak = (surveys: Survey[], scoresById: Record<string, CategoryScores>) => {
  const candidates = streakCandidates(surveys);
  const category = candidates.length > 0 ? lowestCategory(scoresById[candidates[0].id]) : null;
  if (!category) {
    return null;
  }
  let count = 0;
  for (const survey of candidates) {
    if (lowestCategory(scoresById[survey.id]) !== category) {
      break;
    }
    count++;
  }
  return count >= 2 ? { category, count, surveyId: candidates[0].id } : null;
};

// The same rule as the follow-up message on the survey page.
export const isFollowUpRecommended = (survey: Pick<Survey, "status" | "endDate">, now = new Date()) => {
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - FOLLOW_UP_DAYS);
  const cutoffStr = cutoff.toISOString().split("T")[0];
  return survey.status === "finished" && survey.endDate >= cutoffStr;
};

export const followUpSurvey = (surveys: Survey[], now = new Date()) =>
  surveys.filter((s) => isFollowUpRecommended(s, now)).sort(byEndDateDesc)[0] || null;

export const alerts = (
  surveys: Survey[],
  scoresById: Record<string, CategoryScores>,
  recommendations: Recommendation[],
  now = new Date(),
): Alert[] => {
  const list: Alert[] = [];
  const streak = lowestStreak(surveys, scoresById);
  if (streak) {
    list.push({ key: "alertStreak", values: [streak.category, streak.count], href: `/improvement?fromSurvey=${streak.surveyId}` });
  }
  const followUp = followUpSurvey(surveys, now);
  if (followUp) {
    list.push({ key: "alertFollowUp", values: [followUp.surveyName], href: `/survey/${followUp.id}` });
  }
  const quotable = recommendations.filter((r) => r.recommendation);
  if (quotable.length > 0) {
    list.push({ key: "alertRecommendations", values: [quotable.length], href: "/recommendations" });
  }
  return list.slice(0, MAX_ALERTS);
};

// Phases follow findGoalStatus on the server: the first week is planning, the last week conclusion.
export const goalProgress = (goal: Pick<ImprovementGoal, "registered" | "endDate">, now = new Date()) => {
  if (!goal.registered) {
    return { progress: 0, phase: "planning" as GoalPhase };
  }
  const start = new Date(goal.registered).getTime();
  const end = new Date(goal.endDate).getTime();
  const current = now.getTime();
  const progress = end > start ? Math.min(1, Math.max(0, (current - start) / (end - start))) : 1;
  let phase: GoalPhase = "execution";
  if (current > end) {
    phase = "finished";
  } else if (current - start < WEEK) {
    phase = "planning";
  } else if (end - current < WEEK) {
    phase = "conclusion";
  }
  return { progress, phase };
};
